import { SiteQueryResult } from '@graphql/queries/__generated__/site.generated'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import PinterestIcon from '@mui/icons-material/Pinterest'
import TwitterIcon from '@mui/icons-material/Twitter'
import YouTubeIcon from '@mui/icons-material/YouTube'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
// import CreditsSvg from 'images/Credits.svg'
// import PatternPacksSvg from 'images/Pattern_Packs.svg'
import PointsSvg from 'images/Points.svg'
import React from 'react'
import { Link } from 'react-router-dom'
import { ACCOUNT_CREATE_URL, REWARDS_TERMS_FLATPAGE_URL } from '../../urls'
import FooterBlock from './components/FooterBlock'
import FooterSubscribeForm from './components/FooterSubscribeForm'
import FooterTopSocialLinks from './components/FooterTopSocialLinks'
import TumblrIcon from '@icons/TumblrIcon'
import { SHORT_SITE_NAME } from '../../../constants'
import { useTranslation } from 'react-i18next'
import { ToggleButton, ToggleButtonGroup } from '@mui/material'
import WholesaleMode from '@components/WholesaleMode'
import { loadLanguageResource, SUPPORTED_LANGUAGES } from '../../../i18n'

const getSocialIcon = (name: string) => {
  if (name.toLowerCase().includes('pinterest')) {
    return <PinterestIcon sx={{ fontSize: 'inherit' }} />
  } else if (name.toLowerCase().includes('facebook')) {
    return <FacebookIcon sx={{ fontSize: 'inherit' }} />
  } else if (name.toLowerCase().includes('instagram')) {
    return <InstagramIcon sx={{ fontSize: 'inherit' }} />
  } else if (name.toLowerCase().includes('youtube')) {
    return <YouTubeIcon sx={{ fontSize: 'inherit' }} />
  } else if (name.toLowerCase().includes('twitter')) {
    return <TwitterIcon sx={{ fontSize: 'inherit' }} />
  } else if (name.toLowerCase().includes('tumblr')) {
    return <TumblrIcon viewBox='0 0 504 504' sx={{ fontSize: '25px' }} />
  }
}

interface IFooterProps {
  site: SiteQueryResult['data']['siteSettings'] | null
  isStaff: boolean

  handleContactUs(): void

  handleSignUp(): void

  onSubmitSubscribe(values): void
}

const Footer: React.FC<IFooterProps> = ({ site, handleContactUs, handleSignUp, onSubmitSubscribe, isStaff }) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down('sm'))
  const socialLinksBlock = site?.site.footerBlocks.find(block => block.name.toLowerCase().includes('like us'))
  const socialLinks: SiteQueryResult['data']['siteSettings']['site']['footerBlocks'][0]['links'] =
    socialLinksBlock?.links.filter(link => !!link.linkAddress.match(/facebook|pinterest|instagram|tumblr/gm)?.length) ||
    []
  const footerBlocks = site?.site.footerBlocks.filter(block => block.id !== socialLinksBlock?.id) || []

  return (
    <footer className='footer'>
      <FooterTopSocialLinks socialLinks={socialLinks} handleContactUs={handleContactUs} />
      <div className='px-10'>
        <div>
          <div className='default__footer'>
            {!smDown && (
              <div className='flex py-50 mx-auto justify-between'>
                <div>
                  <ul className='p-0 m-0 list-none flex flex-wrap'>
                    {footerBlocks.map(block => (
                      <li key={block.id} className='mb-10 w-1/2 md:w-auto'>
                        <FooterBlock block={block} />
                      </li>
                    ))}
                  </ul>
                  <ul className='px-6 m-0 list-none mt-40 flex items-center'>
                    {socialLinksBlock?.links.map(link => {
                      return (
                        <li key={link.pk} className='px-5'>
                          <a className='text-5xl' href={link.linkAddress} target='_blank'>
                            {getSocialIcon(link.linkAddress)}
                          </a>
                        </li>
                      )
                    })}
                  </ul>
                  {isStaff && (
                    <div className='px-12 my-12'>
                      <ToggleButtonGroup
                        color='primary'
                        value={i18n.language}
                        exclusive
                        onChange={async (e, lng: string) => {
                          await loadLanguageResource(lng)
                          i18n.changeLanguage(lng)
                        }}
                        aria-label='Language'
                        size='small'
                      >
                        {SUPPORTED_LANGUAGES.map(lng => (
                          <ToggleButton key={lng} value={lng}>
                            <span className='uppercase'>{lng}</span>
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </div>
                  )}
                  <div className='px-12 mt-24'>
                    <span className='text-gray text-sm'>
                      {t('copyrightSymbol', '©')} {new Date().getFullYear()} {site?.copyrightText}
                    </span>
                  </div>
                </div>
                <div className='w-360 lg:w-450 flex-shrink-0'>
                  <div>
                    <h3 className='text-13 font-bold mb-8'>{t('footer.likeBeingFirst', 'Like Being First?')}</h3>
                    <div className='text-13 text-gray'>
                      {t('footer.missStyleNews', "Get can't-miss style news, before everybody else.")}
                    </div>
                    <FooterSubscribeForm onSubmit={onSubmitSubscribe} />
                  </div>
                  <WholesaleMode hide>
                    <div className='my-24'>
                      <Divider />
                    </div>

                    <div>
                      <div className='pb-24 flex'>
                        <div className='mr-12'>
                          <img src={PointsSvg} alt='Rewards' width={40} />
                        </div>
                        <div>
                          <h3 className='text-13 font-bold mb-8'>
                            {SHORT_SITE_NAME} {t('rewards', 'Rewards')}
                          </h3>
                          <div className='text-gray text-13'>
                            {t('description.rewards', 'Earn one point for every dollar you spend.')}
                            {t('points', { defaultValue: '{{count}} points', count: 150 })} = $10{' '}
                            {t('reward', 'Reward')}.
                            <br />
                            <Link to={REWARDS_TERMS_FLATPAGE_URL} className='underline hover:underline text-black'>
                              {t('button.learnMore', 'Learn More')}
                            </Link>
                            <Link
                              to={ACCOUNT_CREATE_URL}
                              className='underline hover:underline ml-6 text-black'
                              onClick={e => {
                                e.preventDefault()
                                handleSignUp()
                              }}
                            >
                              {t('button.signUpFree', 'Sign Up Free')}
                            </Link>
                          </div>
                        </div>
                      </div>
                      {/*<Divider />*/}
                      {/*<div className='py-24 flex'>*/}
                      {/*  <div className='mr-12'>*/}
                      {/*    <img src={CreditsSvg} alt='Rewards' width={40} height={40} />*/}
                      {/*  </div>*/}
                      {/*  <div>*/}
                      {/*    <h3 className='text-13 font-bold mb-8'>{t('title.stockUpOnCredits', 'Stock up on Credits')}</h3>*/}
                      {/*    <div className='text-gray text-13'>*/}
                      {/*      {t(*/}
                      {/*        'description.creditsSaveMoneyButBulk',*/}
                      {/*        'Save money and get free bonus credits when you buy in bulk.'*/}
                      {/*      )}*/}
                      {/*      <br />*/}
                      {/*      <Link to={CREDITS_URL} className='underline hover:underline text-black'>*/}
                      {/*        {t('button.getMoreCredits', 'Get More Credits')}*/}
                      {/*      </Link>*/}
                      {/*    </div>*/}
                      {/*  </div>*/}
                      {/*</div>*/}
                      {/*<Divider />*/}
                      {/*<div className='py-24 flex'>*/}
                      {/*  <div className='mr-12'>*/}
                      {/*    <img src={PatternPacksSvg} alt='Rewards' width={40} />*/}
                      {/*  </div>*/}
                      {/*  <div>*/}
                      {/*    <h3 className='text-13 font-bold mb-8'>*/}
                      {/*      {t('title.patternPacks', 'Stock up on Pattern Packs')}*/}
                      {/*    </h3>*/}
                      {/*    <div className='text-gray text-13'>*/}
                      {/*      {t('description.patternPacksSaveMoneyBuyBulk', 'Save money when you buy in bulk.')}*/}
                      {/*      <br />*/}
                      {/*      <Link to={PROFILE_PATTERN_PACKS_URL} className='underline hover:underline text-black'>*/}
                      {/*        {t('button.patternPacks', 'Pattern Packs')}*/}
                      {/*      </Link>*/}
                      {/*    </div>*/}
                      {/*  </div>*/}
                      {/*</div>*/}
                    </div>
                  </WholesaleMode>
                </div>
              </div>
            )}
            {smDown && (
              <div className='flex flex-col py-24 sm:py-50 mx-auto items-start sm:items-center'>
                <div className='max-w-full sm:w-360 sm:flex-shrink-0 mb-24 pb-24'>
                  <div>
                    <h3 className='text-13 font-bold mb-8'>{t('footer.likeBeingFirst', 'Like Being First?')}</h3>
                    <div className='text-13 text-gray'>
                      {t('footer.missStyleNews', "Get can't-miss style news, before everybody else.")}
                    </div>
                    <FooterSubscribeForm onSubmit={onSubmitSubscribe} />
                  </div>
                </div>
                <ul className='p-0 m-0 list-none flex flex-wrap'>
                  {footerBlocks.map(block => (
                    <li key={block.id} className='mb-10 w-1/2'>
                      <FooterBlock block={block} />
                    </li>
                  ))}
                </ul>
                <ul className='p-0 m-0 list-none mt-40 flex items-center -ml-5 sm:ml-0'>
                  {socialLinksBlock?.links.map(link => {
                    return (
                      <li key={link.pk} className='px-5'>
                        <a className='text-5xl' href={link.linkAddress} target={'_blank'}>
                          {getSocialIcon(link.linkAddress)}
                        </a>
                      </li>
                    )
                  })}
                </ul>
                <WholesaleMode hide>
                  <div className='sm:hidden w-full mt-24'>
                    <Divider />
                  </div>
                  <div className='flex flex-col sm:flex-row justify-start sm:justify-center mt-24 sm:mt-36 sm:text-center w-full'>
                    <div className='sm:px-24 flex-1 flex'>
                      <div className='mr-12'>
                        <img src={PointsSvg} alt='Rewards' width={40} />
                      </div>
                      <div>
                        <h3 className='text-13 font-bold mb-8'>
                          {SHORT_SITE_NAME} {t('rewards', 'Rewards')}
                        </h3>
                        <div className='text-gray text-13'>
                          {t('description.rewards', 'Earn one point for every dollar you spend.')}{' '}
                          {t('points', { defaultValue: '{{count}} points', count: 150 })} = $10 {t('reward', 'Reward')}.
                        </div>
                        <div className='flex items-center flex-wrap mt-10 sm:justify-center -ml-6 sm:ml-0'>
                          <Link to={REWARDS_TERMS_FLATPAGE_URL} className='underline hover:underline text-black ml-6'>
                            {t('button.learnMore', 'Learn More')}
                          </Link>
                          <Link
                            to={ACCOUNT_CREATE_URL}
                            className='underline hover:underline ml-6 text-black'
                            onClick={e => {
                              e.preventDefault()
                              handleSignUp()
                            }}
                          >
                            {t('button.signUpFree', 'Sign Up Free')}
                          </Link>
                        </div>
                      </div>
                    </div>
                    <Divider orientation='vertical' flexItem />
                    <div className='sm:hidden my-24 sm:my-0'>
                      <Divider />
                    </div>
                    {/*<div className='sm:px-24 flex-1 flex'>*/}
                    {/*  <div className='mr-12'>*/}
                    {/*    <img src={CreditsSvg} alt='Rewards' width={40} height={40} />*/}
                    {/*  </div>*/}
                    {/*  <div>*/}
                    {/*    <h3 className='text-13 font-bold mb-8'>{t('title.stockUpOnCredits', 'Stock up on Credits')}</h3>*/}
                    {/*    <div className='text-gray text-13'>*/}
                    {/*      {t(*/}
                    {/*        'description.creditsSaveMoneyButBulk',*/}
                    {/*        'Save money and get free bonus credits when you buy in bulk.'*/}
                    {/*      )}*/}
                    {/*    </div>*/}
                    {/*    <div className='flex items-center flex-wrap mt-10 sm:justify-center -ml-6 sm:ml-0'>*/}
                    {/*      <Link to={CREDITS_URL} className='underline hover:underline text-black ml-6'>*/}
                    {/*        {t('button.getMoreCredits', 'Get More Credits')}*/}
                    {/*      </Link>*/}
                    {/*    </div>*/}
                    {/*  </div>*/}
                    {/*</div>*/}
                    {/*<Divider orientation='vertical' flexItem />*/}
                    {/*<div className='sm:hidden my-24 sm:my-0'>*/}
                    {/*  <Divider />*/}
                    {/*</div>*/}
                    {/*<div className='sm:px-24 flex-1 flex'>*/}
                    {/*  <div className='mr-12'>*/}
                    {/*    <img src={PatternPacksSvg} alt='Rewards' width={40} />*/}
                    {/*  </div>*/}
                    {/*  <div>*/}
                    {/*    <h3 className='text-13 font-bold mb-8'>*/}
                    {/*      {t('title.patternPacks', 'Stock up on Pattern Packs')}*/}
                    {/*    </h3>*/}
                    {/*    <div className='text-gray text-13'>*/}
                    {/*      {t('description.patternPacksSaveMoneyBuyBulk', 'Save money when you buy in bulk.')}*/}
                    {/*    </div>*/}
                    {/*    <div className='flex items-center flex-wrap mt-10 sm:justify-center -ml-6 sm:ml-0'>*/}
                    {/*      <Link to={PROFILE_PATTERN_PACKS_URL} className='underline hover:underline text-black ml-6'>*/}
                    {/*        {t('button.patternPacks', 'Pattern Packs')}*/}
                    {/*      </Link>*/}
                    {/*    </div>*/}
                    {/*  </div>*/}
                    {/*</div>*/}
                  </div>
                </WholesaleMode>
                <div className='sm:px-12 mt-36'>
                  <span className='text-gray text-sm'>
                    {t('copyrightSymbol', '©')} {new Date().getFullYear()} {site?.copyrightText}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  )
}

export default React.memo(Footer)
